// import {
//   HttpClient,
//   HttpHeaders,
//   HttpErrorResponse,
// } from '@angular/common/http';
// import {
//   Injectable,
//   Inject,
//   PLATFORM_ID,
//   computed,
//   signal,
// } from '@angular/core';
// import { environment } from '../../environments/environment';
// import { Observable, throwError, of } from 'rxjs';
// import { catchError, retry } from 'rxjs/operators';
// import { isPlatformBrowser } from '@angular/common';

// @Injectable({
//   providedIn: 'root',
// })
// export class AuthService {
//   private userSignal = signal<any>(this.getStoredUser());
//   readonly userPermissions = computed(
//     () => this.userSignal()?.data?.role?.permissions || {}
//   );
//   readonly userRoleId = computed(() => this.userSignal()?.data?.role?.id || 'guest');

//   constructor(
//     private http: HttpClient,
//     @Inject(PLATFORM_ID) private platformId: Object
//   ) {}

//   userLogin(data: any): Observable<any> {
//     return this.http.post(`${environment.api_URL}/auth/login`, data).pipe(
//       retry(1), // Retry once on failure
//       catchError((error: HttpErrorResponse) => {
//         // Handle server-side rendering gracefully
//         if (!isPlatformBrowser(this.platformId)) {
//           return of({ status: false, message: 'SSR mode' });
//         }

//         // Return a structured error response
//         return throwError(() => ({
//           status: false,
//           message: this.getErrorMessage(error),
//           error: error,
//         }));
//       })
//     );
//   }

//   private async getStoredUser() {
//     if (isPlatformBrowser(this.platformId)) {
//       const data = localStorage.getItem('Admin_User'); // No await needed
//       return data ? JSON.parse(data) : null;
//     }
    
//     // Return null if running on the Server (SSR)
//     return null;
//   }

//   hasPermission(perm: string): boolean {
//     let roleId:any = this.userRoleId();
//     let permissions:any = {}
//     if (roleId === 'guest') {
//       const storedUser = localStorage.getItem('Admin_User');
//       if (storedUser) {
//         const parsed = JSON.parse(storedUser);
//         roleId = parsed.data?.role?.id || parsed.data?.role;
//         permissions = parsed.data.role.permissions
//       }
//     }
//     if (roleId == 'super_admin') return true;
//       return !!permissions[perm];

//   }

//   setUser(userData: any) {
//     localStorage.setItem('user', JSON.stringify(userData));
//     this.userSignal.set(userData);
//   }

//   private getErrorMessage(error: HttpErrorResponse): string {
//     if (error.status === 0) {
//       return 'Network error. Please check your connection.';
//     } else if (error.status === 401) {
//       return 'Invalid credentials.';
//     } else if (error.status === 403) {
//       return 'Access denied.';
//     } else if (error.status >= 500) {
//       return 'Server error. Please try again later.';
//     } else {
//       return 'Login failed. Please try again.';
//     }
//   }
// }

import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, Inject, PLATFORM_ID, computed, signal } from '@angular/core';
import { environment } from '../../environments/environment';
import { Observable, throwError, of, BehaviorSubject } from 'rxjs';
import { catchError, retry, tap } from 'rxjs/operators';
import { isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';

@Injectable({ providedIn: 'root' })
export class AuthService {

  // ── Signals ──────────────────────────────────────
  private userSignal = signal<any>(this.getStoredUser());
  readonly userPermissions = computed(() => this.userSignal()?.data?.role?.permissions ?? {});
  readonly userRoleId = computed(() => this.userSignal()?.data?.role?.id ?? 'guest');

  // ── Refresh token state ───────────────────────────
  private _isRefreshing = false;
  private _refreshSubject = new BehaviorSubject<string | null>(null);

  get isRefreshingToken(): boolean { return this._isRefreshing; }
  get refreshSubject(): BehaviorSubject<string | null> { return this._refreshSubject; }
  setRefreshing(val: boolean): void { this._isRefreshing = val; }

  private readonly USER_KEY = 'Admin_User';

  constructor(
    private http: HttpClient,
    private router: Router,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  // ─────────────────────────────────────────────────
  // BROWSER GUARD
  // ─────────────────────────────────────────────────

  isBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }

  // ─────────────────────────────────────────────────
  // USER STORAGE  (sync — no async needed here)
  // ─────────────────────────────────────────────────

  private getStoredUser(): any {
    if (!isPlatformBrowser(this.platformId)) return null;
    try {
      const raw = localStorage.getItem(this.USER_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  setUser(userData: any): void {
    if (this.isBrowser()) {
      localStorage.setItem(this.USER_KEY, JSON.stringify(userData));
    }
    this.userSignal.set(userData);
  }

  getUser(): any {
    return this.userSignal() ?? this.getStoredUser();
  }

  // ── Token helpers ─────────────────────────────────

  getAccessToken(): string | null {
    const user = this.getStoredUser();
    // supports both token shapes: { data: { token } } and { data: { accessToken } }
    return user?.data?.accessToken ?? user?.data?.token ?? null;
  }

  getRefreshToken(): string | null {
    if (!this.isBrowser()) return null;
    return localStorage.getItem('refreshToken');
  }

  getUserId(): string | null {
    const user = this.getStoredUser();
    return user?.data?._id ?? user?.data?.id ?? null;
  }

  saveTokens(accessToken: string, refreshToken: string): void {
    if (!this.isBrowser()) return;
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);

    // Also update the token inside Admin_User so jwt interceptor stays in sync
    const user = this.getStoredUser();
    if (user?.data) {
      user.data.accessToken = accessToken;
      user.data.token = accessToken;
      this.setUser(user);
    }
  }

  isLoggedIn(): boolean {
    return !!this.getAccessToken();
  }

  // ─────────────────────────────────────────────────
  // SESSION
  // ─────────────────────────────────────────────────

  clearSession(): void {
    if (!this.isBrowser()) return;
    localStorage.removeItem(this.USER_KEY);
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    this.userSignal.set(null);
    this.router.navigate(['/'], { replaceUrl: true });
  }

  // ─────────────────────────────────────────────────
  // PERMISSIONS
  // ─────────────────────────────────────────────────

  hasPermission(perm: string): boolean {
    const user = this.getStoredUser();
    const roleId = user?.data?.role?.id ?? user?.data?.role;
    if (roleId === 'super_admin') return true;
    const permissions = user?.data?.role?.permissions ?? {};
    return !!permissions[perm];
  }

  // ─────────────────────────────────────────────────
  // REFRESH TOKEN
  // ─────────────────────────────────────────────────

  refreshTokens(): Observable<any> {
    const userId = this.getUserId();
    const refreshToken = this.getRefreshToken();

    if (!userId || !refreshToken) {
      this.clearSession();
      return throwError(() => new Error('No refresh token available'));
    }

    return this.http
      .post<any>(`${environment.api_URL}/auth/refresh`, { userId, refreshToken })
      .pipe(
        tap((res) => {
          if (res?.accessToken && res?.refreshToken) {
            this.saveTokens(res.accessToken, res.refreshToken);
            this._refreshSubject.next(res.accessToken);
          }
        }),
        catchError((err) => {
          this.clearSession();
          return throwError(() => err);
        })
      );
  }

  // ─────────────────────────────────────────────────
  // API CALLS
  // ─────────────────────────────────────────────────

  userLogin(data: any): Observable<any> {
    return this.http.post(`${environment.api_URL}/auth/login`, data).pipe(
      tap((res: any) => {
        if (res?.data) {
          this.setUser(res);
          // Save tokens separately for refresh strategy
          if (res.data.refreshToken) {
            localStorage.setItem('refreshToken', res.data.refreshToken);
          }
        }
      }),
      catchError(this.handleError.bind(this))
    );
  }

  getCountryCode(): Observable<any> {
    return this.http.get(`${environment.api_URL}/dashboard/contCode`).pipe(
      catchError(this.handleError.bind(this))
    );
  }

  logout(): void {
    this.http.post(`${environment.api_URL}/auth/logout`, {}).subscribe({
      complete: () => this.clearSession(),
      error: () => this.clearSession(),
    });
  }

  // ─────────────────────────────────────────────────
  // ERROR HANDLER
  // ─────────────────────────────────────────────────

  private handleError(error: HttpErrorResponse): Observable<never> {
    if (!this.isBrowser()) return of({ status: false, message: 'SSR mode' }) as any;
    return throwError(() => ({
      status: false,
      message: this.getErrorMessage(error),
      error,
    }));
  }

  private getErrorMessage(error: HttpErrorResponse): string {
    if (error?.error?.message) return error.error.message;
    switch (error.status) {
      case 0:   return 'Network error. Please check your connection.';
      case 400: return 'Bad request.';
      case 401: return 'Invalid credentials.';
      case 403: return 'Access denied.';
      case 500: return 'Server error. Please try again later.';
      default:  return 'Login failed. Please try again.';
    }
  }
}
